import { useState, useCallback, useEffect, useMemo } from 'react';

import { useTabsHeader } from '../../../tabs-header/hooks/use-tabs-header';
import type { TPanelHeaderDesktopV2Props } from '../../models';
import type { IOnChangeCallback } from '../../../../../models';
import withIconStyles from '../styles/with-icon.css';
import defaultStyles from '../styles/styles.css';

type TUseTabsPanelV2 = TPanelHeaderDesktopV2Props;

export const useTabsPanelV2 = ({
    onChange,
    selectedID,
    theme = 'light',
    titles,
    widthTab,
}: TUseTabsPanelV2) => {
    const [biggerTabWidth, setBiggerTabWidth] = useState<number>(0);

    const handleOnChange = useCallback(
        (params: IOnChangeCallback) => {
            onChange(params);
        },
        [onChange]
    );
    const { headerContainer, headerList, selectedTab, withIcon, ...tabsProps } = useTabsHeader({
        onChange: handleOnChange,
        titles,
        theme,
        selectedID,
    });

    useEffect(() => {
        const selectedTabOffset = selectedTab.current?.offsetLeft ?? 0;
        const selectedTabWidth = selectedTab.current?.offsetWidth ?? 0;
        const headerListWidth = headerContainer.current?.offsetWidth ?? 0;

        headerContainer.current?.scroll({
            behavior: 'smooth',
            left:
                selectedTabOffset -
                Math.round(headerListWidth / 2) +
                Math.round(selectedTabWidth / 2),
        });
    }, [headerContainer, headerList, selectedTab]);

    const tabWidthList: number[] = useMemo(() => [], []);

    useEffect(() => {
        if (headerList.current?.children.length && widthTab === 'equal') {
            for (let i = 1; headerList.current?.children.length > i; i++) {
                if (headerList.current.children[i].role === 'tab') {
                    tabWidthList.push(headerList.current.children[i].getBoundingClientRect().width);
                }
            }
        }
    }, [headerList, tabWidthList, widthTab]);

    useEffect(() => {
        setBiggerTabWidth(tabWidthList.sort((a, b) => b - a)[0]);
    }, [biggerTabWidth, tabWidthList]);

    const onTabMouseDownHandler = useCallback(
        ({ target }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            (target as HTMLElement).classList.add(
                withIcon ? withIconStyles.clicked : defaultStyles.clicked
            );
            (target as HTMLElement).classList.add(
                withIcon ? withIconStyles.external : defaultStyles.external
            );
        },
        [withIcon]
    );

    const onTabMouseUpHandler = useCallback(
        ({ target }: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            (target as HTMLElement).classList.remove(
                withIcon ? withIconStyles.clicked : defaultStyles.clicked
            );
            (target as HTMLElement).classList.remove(
                withIcon ? withIconStyles.external : defaultStyles.external
            );
        },
        [withIcon]
    );

    return {
        headerContainer,
        headerList,
        onTabMouseDownHandler,
        onTabMouseUpHandler,
        selectedTab,
        tabWidthList,
        tabsProps,
        withIcon,
    };
};
